import { useEffect, useState } from 'react'

// useLocalStorage — the console's first localStorage consumer (S5 keys
// per-group collapse state by category). Crash-safe by design: the read is a
// lazy guarded JSON.parse (covers blocked storage, quota, and malformed JSON),
// and the write useEffect swallows failures so private-mode / quota errors
// never throw — the React state stays authoritative as an in-memory fallback.
export function useLocalStorage<T>(
  key: string,
  initial: T,
): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw != null ? (JSON.parse(raw) as T) : initial
    } catch {
      return initial
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // In-memory fallback: state is still authoritative; a write failure
      // (private-mode / quota) must not throw.
    }
  }, [key, value])

  return [value, setValue]
}
